'use client';

import * as React from 'react';
import { Check, ChevronsUpDown, Plus, Plane, X, Loader2 } from 'lucide-react';
import { useFirestore } from '@/firebase';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { AircraftModel } from '@/models/aircraft-model';
import { getAircraftModels } from '@/lib/aircraft-models';
import { CreateAircraftModelDialog } from './create-aircraft-model-dialog';

interface SelectedItem {
  id: string;
  name: string;
  isKnownModel: boolean; // true if from aircraftModels collection, false if custom/unrecognized
}

interface AircraftModelComboboxProps {
  value: string[];
  valueNames?: string[]; // Names corresponding to value IDs (for display of unrecognized models)
  onChange: (ids: string[], names: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function AircraftModelCombobox({
  value,
  valueNames = [],
  onChange,
  disabled = false,
  placeholder = 'Select aircraft models...',
}: AircraftModelComboboxProps) {
  const firestore = useFirestore();
  const [open, setOpen] = React.useState(false);
  const [models, setModels] = React.useState<AircraftModel[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState('');
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);

  // Load models when popover opens
  React.useEffect(() => {
    if (!open || !firestore) return;

    const loadModels = async () => {
      setLoading(true);
      try {
        const loaded = await getAircraftModels(firestore, { sortBy: 'usageCount' });
        setModels(loaded);
      } catch (error) {
        console.error('Failed to load aircraft models:', error);
      } finally {
        setLoading(false);
      }
    };

    loadModels();
  }, [open, firestore]);

  // Get selected items with info about whether they're known models
  const selectedItems = React.useMemo((): SelectedItem[] => {
    return value.map((id, index) => {
      const model = models.find(m => m.id === id);
      if (model) {
        return { id, name: model.name, isKnownModel: true };
      }
      // Not found in models - use the provided name or fallback
      const name = valueNames[index] || id;
      return { id, name, isKnownModel: false };
    });
  }, [models, value, valueNames]);

  // Get selected models that ARE in the models list (for internal logic)
  const selectedModels = React.useMemo(() => {
    return models.filter(m => value.includes(m.id));
  }, [models, value]);

  // Filter models based on search
  const filteredModels = React.useMemo(() => {
    if (!searchValue) return models;
    const search = searchValue.toLowerCase();
    return models.filter(
      m =>
        m.name.toLowerCase().includes(search) ||
        m.manufacturer?.toLowerCase().includes(search)
    );
  }, [models, searchValue]);

  const handleSelect = (modelId: string) => {
    const model = models.find(m => m.id === modelId);
    if (!model) return;

    let newIds: string[];
    let newNames: string[];

    if (value.includes(modelId)) {
      // Remove
      newIds = value.filter(id => id !== modelId);
      newNames = selectedModels
        .filter(m => m.id !== modelId)
        .map(m => m.name);
    } else {
      // Add
      newIds = [...value, modelId];
      newNames = [...selectedModels.map(m => m.name), model.name];
    }

    onChange(newIds, newNames);
  };

  const handleRemove = (itemId: string) => {
    const newIds = value.filter(id => id !== itemId);
    const newNames = selectedItems
      .filter(item => item.id !== itemId)
      .map(item => item.name);
    onChange(newIds, newNames);
  };

  const handleModelCreated = (newModel: AircraftModel) => {
    // Add to local list
    setModels(prev => [newModel, ...prev]);
    // Select it
    onChange([...value, newModel.id], [...selectedItems.map(item => item.name), newModel.name]);
    setCreateDialogOpen(false);
  };

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              'w-full justify-between',
              !value.length && 'text-muted-foreground'
            )}
            disabled={disabled}
          >
            <span className="flex items-center gap-2">
              <Plane className="h-4 w-4" />
              {value.length > 0
                ? `${value.length} aircraft selected`
                : placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search aircraft models..."
              value={searchValue}
              onValueChange={setSearchValue}
            />
            <CommandList>
              {loading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="ml-2 text-sm text-muted-foreground">Loading...</span>
                </div>
              ) : (
                <>
                  <CommandEmpty>
                    <div className="text-center py-2">
                      <p className="text-sm text-muted-foreground">No models found.</p>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2"
                        onClick={() => {
                          setCreateDialogOpen(true);
                          setOpen(false);
                        }}
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Create "{searchValue}"
                      </Button>
                    </div>
                  </CommandEmpty>
                  <CommandGroup>
                    {filteredModels.map(model => (
                      <CommandItem
                        key={model.id}
                        value={model.id}
                        onSelect={() => handleSelect(model.id)}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            value.includes(model.id) ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        <div className="flex flex-col flex-1">
                          <span>{model.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {model.type}
                            {model.manufacturer && ` • ${model.manufacturer}`}
                          </span>
                        </div>
                        {model.usageCount > 0 && (
                          <span className="text-xs text-muted-foreground">
                            {model.usageCount} uses
                          </span>
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => {
                        setCreateDialogOpen(true);
                        setOpen(false);
                      }}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Create new aircraft model
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Selected items - badges for known models, text for custom/unrecognized */}
      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedItems.map(item => (
            item.isKnownModel ? (
              <Badge key={item.id} variant="secondary" className="pr-1">
                {item.name}
                <button
                  type="button"
                  onClick={() => handleRemove(item.id)}
                  className="ml-1 hover:text-destructive rounded-full"
                  disabled={disabled}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ) : (
              <span
                key={item.id}
                className="inline-flex items-center gap-1 text-sm text-muted-foreground bg-muted/50 px-2 py-0.5 rounded border border-dashed"
              >
                {item.name}
                <button
                  type="button"
                  onClick={() => handleRemove(item.id)}
                  className="hover:text-destructive"
                  disabled={disabled}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )
          ))}
        </div>
      )}

      <CreateAircraftModelDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreated={handleModelCreated}
        initialName={searchValue}
      />
    </div>
  );
}
